// StatusTab.jsx
// Live Status tab - reads from TCC_Platform_Tracker.xlsx and TCC_Phase_Status.xlsx
// Status tab remains visible to unauthenticated users with static fallback data

import { useState, useEffect, useCallback } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import { fetchPlatformTracker, fetchPhaseStatus, patchTrackerTask, patchBlockerRow, patchPhaseTask } from "./platformService";
import { isOnAcl, resolveOwnerEmails, sendStatusReminder, REMINDER_ACL } from "./mailService";
import { useOrgDirectory } from "./OrgDirectoryContext";

const CACHE_URL = "/api/cache";

const C = {
  dark: "#1a1a2e", gold: "#c9a84c", white: "#fff", surface: "#f8f7f4",
  border: "#e5e7eb", muted: "#6b7280", text: "#374151",
  green: "#27ae60", orange: "#e67e22", red: "#c0392b",
  blue: "#2980b9", purple: "#8e44ad", yellow: "#fde68a", yellowBg: "#fef3c7",
};

const PHASE_COLORS = {
  "Phase 1 - Foundation": C.gold,
  "Phase 2 - Integration": C.blue,
  "Phase 3 - Intelligence": C.green,
  "Phase 4 - AI Layer": C.purple,
  "CIM Readiness": C.red,
};

function statusColor(s) {
  if (s === "done") return C.green;
  if (s === "in-progress") return C.orange;
  if (s === "not-ready") return C.red;
  return "#9ca3af";
}
function statusLabel(s) {
  if (s === "done") return "Done";
  if (s === "in-progress") return "In Progress";
  if (s === "not-ready") return "Not Ready";
  return "Not Started";
}

function Spinner({ small }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, color: C.muted }}>
      <div style={{ width: small ? 12 : 16, height: small ? 12 : 16, border: "2px solid #e5e7eb", borderTop: "2px solid " + C.gold, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      {!small && <span style={{ fontSize: 12 }}>Loading from SharePoint...</span>}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// Static fallback data for unauthenticated view




function PhaseAccordion({ phase, defaultOpen, blockers, selectedItems, toggleItem, userOnAcl, getToken, projectConfig, loadData }) {
  const [open, setOpen] = useState(defaultOpen || false);
  const color = PHASE_COLORS[phase.name] || C.blue;

  const tracks = {};
  phase.items.forEach(function(item) {
    if (!tracks[item.track]) tracks[item.track] = [];
    tracks[item.track].push(item);
  });

  const total = phase.items.length;
  const done = phase.items.filter(function(i) { return i.status === "done"; }).length;
  const inProg = phase.items.filter(function(i) { return i.status === "in-progress"; }).length;
  const atRisk = phase.items.filter(function(i) { return i.status === "at-risk"; }).length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: 8, marginBottom: 8, overflow: "hidden" }}>
      <div onClick={function() { setOpen(!open); }} style={{ padding: "12px 16px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", background: open ? C.dark : C.white }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }} />
          <span style={{ fontWeight: 700, color: open ? C.gold : C.dark }}>{phase.name}</span>
          {phase.timeline && <span style={{ fontSize: 11, color: open ? "#9ca3af" : C.muted }}>{phase.timeline}</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {total > 0 && (
            <div style={{ display: "flex", gap: 8, fontSize: 11 }}>
              <span style={{ color: C.green, fontWeight: 600 }}>{done} done</span>
              <span style={{ color: C.orange, fontWeight: 600 }}>{inProg} in progress</span>
              {atRisk > 0 && <span style={{ color: C.red, fontWeight: 600 }}>{atRisk} at risk</span>}
            </div>
          )}
          <span style={{ color: open ? C.gold : "#9ca3af", fontSize: 16, fontWeight: 700 }}>{open ? "-" : "+"}</span>
        </div>
      </div>

      {open && (
        <div style={{ padding: 16 }}>
          {total > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 11, color: C.muted }}>
                <span>Phase progress</span>
                <span>{done} of {total} complete ({pct}%)</span>
              </div>
              <div style={{ background: "#f3f4f6", borderRadius: 4, height: 6 }}>
                <div style={{ width: pct + "%", background: color, height: "100%", borderRadius: 4, transition: "width 0.4s" }} />
              </div>
            </div>
          )}

          {Object.entries(tracks).map(function([track, items]) {
            return (
              <div key={track} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: color, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>{track}</div>
                {items.map(function(item, i) {
                  return (
                    <TaskRow key={i} item={item} userOnAcl={userOnAcl}
                      selected={!!(selectedItems && selectedItems[phase.name + "::" + (item.task || item.item)])}
                      onToggle={function() { toggleItem && toggleItem(phase.name + "::" + (item.task || item.item)); }}
                      onSave={function(updates) {
                        return getToken().then(function(tok) {
                          return patchPhaseTask(tok, projectConfig, phase.name, item.rowIndex, Object.assign({}, updates, { taskName: item.task || item.item }));
                        }).then(function() { loadData(); }).catch(function(e) { alert("Save failed: " + e.message); });
                      }}
                    />
                  );
                })}
              </div>
            );
          })}

          {phase.items.length === 0 && (
            <div style={{ fontSize: 12, color: C.muted, padding: "20px 0", textAlign: "center" }}>No items loaded for this phase.</div>
          )}

          {blockers && blockers.length > 0 && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid " + C.border }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.red, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>{blockers.length} Active Blockers</div>
              {blockers.map(function(b, i) {
                return <BlockerRow key={i} blocker={b} userOnAcl={userOnAcl}
                  onSave={function(updates) {
                    return getToken().then(function(tok) {
                      return patchBlockerRow(tok, projectConfig, b.rowIndex, Object.assign({}, updates, { blockerName: b.blocker }));
                    }).then(function() { loadData(); }).catch(function(e) { alert("Save failed: " + e.message); });
                  }} />;
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}



function BlockerRow({ blocker: b, userOnAcl, onSave }) {
  const [editing, setEditing] = useState(false);
  const [editDesc, setEditDesc] = useState(b.description || "");
  const [editOwner, setEditOwner] = useState(b.owner || "");
  const [editDue, setEditDue] = useState(b.due || "");
  function handleSave() {
    onSave({ description: editDesc, owner: editOwner, due: editDue });
    setEditing(false);
  }
  function handleCancel() {
    setEditDesc(b.description || ""); setEditOwner(b.owner || ""); setEditDue(b.due || ""); setEditing(false);
  }
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 8, padding: "8px 10px", background: C.yellowBg, borderRadius: 6, borderLeft: "3px solid " + C.red }}>
      <span style={{ color: C.red, fontWeight: 700, flexShrink: 0 }}>!</span>
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ fontWeight: 600, color: "#78350f", fontSize: 12, marginBottom: 2 }}>{b.blocker}</div>
          {userOnAcl && !editing && (
            <button onClick={function() { setEditing(true); }} style={{ fontSize: 10, padding: "1px 7px", borderRadius: 4, border: "1px solid #fde68a", background: "transparent", cursor: "pointer", color: "#92400e", marginLeft: 8, flexShrink: 0 }}>Edit</button>
          )}
        </div>
        {!editing && <div style={{ fontSize: 11, color: "#92400e", lineHeight: 1.5 }}>{b.description}{b.owner ? " Owner: " + b.owner + "." : ""}{b.due ? " Due: " + b.due + "." : ""}</div>}
        {editing && (
          <div style={{ marginTop: 6 }}>
            <textarea value={editDesc} onChange={function(e) { setEditDesc(e.target.value); }} placeholder="Description"
              style={{ width: "100%", fontSize: 11, padding: "4px 6px", borderRadius: 4, border: "1px solid #fde68a", resize: "vertical", minHeight: 40, fontFamily: "inherit", background: "#fffbeb", boxSizing: "border-box", marginBottom: 6 }} />
            <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              <input value={editOwner} onChange={function(e) { setEditOwner(e.target.value); }} placeholder="Owner"
                style={{ flex: 1, fontSize: 11, padding: "4px 6px", borderRadius: 4, border: "1px solid #fde68a", background: "#fffbeb" }} />
              <input value={editDue} onChange={function(e) { setEditDue(e.target.value); }} placeholder="Due date"
                style={{ flex: 1, fontSize: 11, padding: "4px 6px", borderRadius: 4, border: "1px solid #fde68a", background: "#fffbeb" }} />
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={handleSave} style={{ fontSize: 10, padding: "3px 12px", borderRadius: 4, border: "none", background: "#78350f", color: "#fef3c7", fontWeight: 700, cursor: "pointer" }}>Save</button>
              <button onClick={handleCancel} style={{ fontSize: 10, padding: "3px 10px", borderRadius: 4, border: "1px solid #fde68a", background: "transparent", cursor: "pointer", color: "#92400e" }}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const STATUS_OPTIONS = [
  { value: "not-started", label: "Not Started" },
  { value: "in-progress", label: "In Progress" },
  { value: "done",        label: "Done" },
  { value: "at-risk",     label: "At Risk" },
];

function TaskRow({ item, userOnAcl, selected, onToggle, onSave }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editStatus, setEditStatus] = useState(item.status);
  const [editRisk, setEditRisk] = useState(item.risk);
  const [editNote, setEditNote] = useState(item.note || "");
  const [editOwner, setEditOwner] = useState(item.owner || "");
  function handleSave() {
    setSaving(true);
    onSave({ status: editStatus, risk: editRisk, note: editNote, owner: editOwner });
    setEditing(false);
    setSaving(false);
  }
  function handleCancel() {
    setEditStatus(item.status);
    setEditRisk(item.risk);
    setEditNote(item.note || "");
    setEditOwner(item.owner || "");
    setEditing(false);
  }
  return (
    <div style={{ background: C.white, border: "1px solid " + (item.risk ? C.yellow : C.border), borderLeft: "4px solid " + statusColor(editing ? editStatus : item.status), borderRadius: 8, padding: "10px 14px", marginBottom: 8 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        {userOnAcl && <input type="checkbox" checked={selected} onChange={onToggle} style={{ marginTop: 3, cursor: "pointer", flexShrink: 0 }} />}
        <div style={{ width: 14, height: 14, borderRadius: "50%", background: statusColor(editing ? editStatus : item.status), flexShrink: 0, marginTop: 3 }} />
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6, marginBottom: 4 }}>
            <div style={{ fontWeight: 500, fontSize: 13 }}>{item.task}</div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {!editing && item.risk && <span style={{ background: C.yellowBg, color: "#92400e", fontSize: 10, padding: "1px 6px", borderRadius: 3, fontWeight: 700 }}>AT RISK</span>}
              {item.owner && <span style={{ fontSize: 11, color: C.muted }}>{item.owner}</span>}
              {!editing && <span style={{ fontSize: 11, color: statusColor(item.status), fontWeight: 600 }}>{statusLabel(item.status)}</span>}
              {userOnAcl && !editing && (
                <button onClick={function() { setEditing(true); }} style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, border: "1px solid " + C.border, background: C.surface, cursor: "pointer", color: C.muted }}>
                  Edit
                </button>
              )}
            </div>
          </div>
          {!editing && <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>{item.note}</div>}
          {editing && (
            <div style={{ marginTop: 6 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
                <select value={editStatus} onChange={function(e) { setEditStatus(e.target.value); }}
                  style={{ fontSize: 12, padding: "4px 8px", borderRadius: 4, border: "1px solid " + C.border, background: C.white, color: statusColor(editStatus), fontWeight: 600, cursor: "pointer" }}>
                  {STATUS_OPTIONS.map(function(o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
                </select>
                <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: C.muted, cursor: "pointer" }}>
                  <input type="checkbox" checked={editRisk} onChange={function(e) { setEditRisk(e.target.checked); }} /> At Risk
                </label>
              </div>
              <textarea value={editNote} onChange={function(e) { setEditNote(e.target.value); }}
                placeholder="Add a note..."
                style={{ width: "100%", fontSize: 12, padding: "6px 8px", borderRadius: 4, border: "1px solid " + C.border, resize: "vertical", minHeight: 52, fontFamily: "inherit", color: C.text, boxSizing: "border-box" }} />
              <select value={editOwner} onChange={function(e) { setEditOwner(e.target.value); }}
                style={{ width: "100%", fontSize: 12, padding: "6px 8px", borderRadius: 4, border: "1px solid " + C.border, color: C.text, boxSizing: "border-box", marginTop: 6, background: C.white }}>
                <option value="">-- Owner --</option>
                <option value="Dan Frasco">Dan Frasco</option>
                <option value="Martin Lopez">Martin Lopez</option>
                <option value="Vanessa Fucciani">Vanessa Fucciani</option>
                <option value="Mike Dansby">Mike Dansby</option>
                <option value="Rachel Hopper">Rachel Hopper</option>
                <option value="Dave Smith">Dave Smith</option>
                <option value="Sab Guerriero">Sab Guerriero</option>
                <option value="Dan Frasco / Mike Dansby">Dan Frasco / Mike Dansby</option>
                <option value="Dan Frasco / Rachel Hopper">Dan Frasco / Rachel Hopper</option>
                <option value="Martin Lopez / Vanessa Fucciani">Martin Lopez / Vanessa Fucciani</option>
                <option value="Burke / Monica">Burke / Monica</option>
                <option value="Dan Frasco/Dave Smith">Dan Frasco/Dave Smith</option>
                <option value="Martin Lopez/Vanessa Fucciani">Martin Lopez/Vanessa Fucciani</option>
                <option value="TBD">TBD</option>
              </select>
              <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                <button onClick={handleSave} disabled={saving}
                  style={{ fontSize: 11, padding: "4px 14px", borderRadius: 4, border: "none", background: C.dark, color: C.gold, fontWeight: 700, cursor: "pointer" }}>
                  {saving ? "Saving..." : "Save"}
                </button>
                <button onClick={handleCancel}
                  style={{ fontSize: 11, padding: "4px 12px", borderRadius: 4, border: "1px solid " + C.border, background: C.white, cursor: "pointer", color: C.muted }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function StatusTab({ onCountsUpdate, projectConfig }) {
  const { instance, accounts } = useMsal();
  const { orgUsers } = useOrgDirectory();

  async function getToken() {
    if (!accounts[0]) throw new Error("Not authenticated");
    const resp = await instance.acquireTokenSilent({
      ...loginRequest,
      scopes: [...(loginRequest.scopes || []), "Mail.Send"],
      account: accounts[0],
    });
    return resp.accessToken;
  }
  const isAuthenticated = useIsAuthenticated();
  const [tasks, setTasks] = useState([]);
  const [blockers, setBlockers] = useState([]);
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [cachedAt, setCachedAt] = useState(null);
  const [usingCache, setUsingCache] = useState(false);
  const [error, setError] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderCc, setReminderCc] = useState(true);
  const [extraRecipients, setExtraRecipients] = useState({});
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderSent, setReminderSent] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifyTeams, setNotifyTeams] = useState(false);
  const [notifySms, setNotifySms] = useState(false);
  const [teamsResult, setTeamsResult] = useState(null);
  const [emailResult, setEmailResult] = useState(null);

  // ACL check
  const userOnAcl = isOnAcl(accounts[0]);
  const senderName = accounts[0]?.name || "A team member";

  // Toggle item selection
  function toggleItem(itemKey) {
    setSelectedItems(function(prev) {
      const next = Object.assign({}, prev);
      if (next[itemKey]) delete next[itemKey];
      else next[itemKey] = true;
      return next;
    });
  }

  function makeItemKey(item, source) {
    return (source || "tracker") + "::" + (item.task || item.item || "");
  }

  const selectedCount = Object.keys(selectedItems).length;
  // Collect all selectable items - tasks plus phase items
  const allItems = tasks.map(function(t) {
    return Object.assign({}, t, { _key: "tracker::" + t.task });
  });
  phases.forEach(function(phase) {
    (phase.items || []).forEach(function(item) {
      const task = item.task || item.item;
      allItems.push(Object.assign({}, item, { task: task, phaseName: phase.name, _key: phase.name + "::" + task }));
    });
  });
  const selectedTaskItems = allItems.filter(function(t) {
    return selectedItems[t._key];
  });

  // Build recipient list for modal
  function buildRecipients() {
    const toMap = {};
    for (const item of selectedTaskItems) {
      const resolved = resolveOwnerEmails(item.owner, orgUsers);
      for (const r of resolved) {
        toMap[r.email] = r;
      }
    }
    return Object.values(toMap);
  }

  async function handleSendReminder(accessToken) {
    setSendingReminder(true);
    setTeamsResult(null);
    try {
      const to = buildRecipients();
      const extra = Object.keys(extraRecipients)
        .filter(function(k) { return extraRecipients[k]; })
        .map(function(email) { return { email: email, name: email }; });
      const cc = [];
      if (reminderCc && accounts[0]) {
        cc.push({ email: accounts[0].username, name: accounts[0].name });
      }
      for (const r of extra) cc.push(r);

      // Send email if selected
      if (notifyEmail && to.length > 0) {
        try {
          await sendStatusReminder(accessToken, {
            toRecipients: to,
            ccRecipients: cc,
            items: selectedTaskItems,
            senderName: senderName,
          });
          setEmailResult("sent");
        } catch (e) {
          setEmailResult("failed");
          console.error("Email failed:", e.message);
        }
      }

      // Send Teams notification if selected
      if (notifyTeams) {
        try {
          // notify-server checks the sender against NOTIFY_SEND_ACL (this
          // button's REMINDER_ACL), so it needs the user's bearer token.
          const teamsRes = await fetch("/api/notify/teams", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + accessToken },
            body: JSON.stringify({
              type: "status_reminder",
              items: selectedTaskItems,
              sender: { name: senderName, email: accounts[0]?.username },
            }),
          });
          const teamsData = await teamsRes.json();
          setTeamsResult(teamsData.success ? "sent" : "failed");
        } catch (e) {
          setTeamsResult("failed");
          console.error("Teams notify failed:", e.message);
        }
      }

      // Send SMS if selected
      if (notifySms) {
        try {
          const smsRecipients = buildRecipients().filter(function(r) { return r.phone; });
          if (smsRecipients.length > 0) {
            // notify-server requires a TrapperKeeper-TCC-Admin Graph token
            // here, and only texts numbers with recorded SMS consent.
            await fetch("/api/notify/sms", {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: "Bearer " + accessToken },
              body: JSON.stringify({
                type: "status_reminder",
                recipients: smsRecipients,
                items: selectedTaskItems,
                sender: { name: senderName },
              }),
            });
          }
        } catch (e) {
          console.error("SMS failed:", e.message);
        }
      }

      setReminderSent(true);
      setSelectedItems({});
      setTimeout(function() {
        setShowReminderModal(false);
        setReminderSent(false);
        setTeamsResult(null);
        setEmailResult(null);
        setNotifyTeams(false);
        setNotifySms(false);
        setNotifyEmail(true);
      }, 2500);
    } catch (err) {
      alert("Failed to send: " + err.message);
    } finally {
      setSendingReminder(false);
    }
  }

  // Load from cache (for unauthenticated users or as fallback)
  const loadFromCache = useCallback(async function() {
    try {
      const res = await fetch(CACHE_URL);
      if (!res.ok) return false;
      const cached = await res.json();
      if (!cached.cached || !cached.data) return false;
      const d = cached.data;
      if (d.tasks && d.tasks.length > 0) setTasks(d.tasks);
      if (d.blockers && d.blockers.length > 0) setBlockers(d.blockers);
      if (d.phases && d.phases.length > 0) setPhases(d.phases);
      setCachedAt(cached.lastUpdated);
      setUsingCache(true);
      if (onCountsUpdate && d.tasks) {
        onCountsUpdate({
          done: d.tasks.filter(function(i) { return i.status === "done"; }).length,
          inProg: d.tasks.filter(function(i) { return i.status === "in-progress"; }).length,
          notStarted: d.tasks.filter(function(i) { return i.status === "not-started"; }).length,
          atRisk: d.tasks.filter(function(i) { return i.risk; }).length,
          total: d.tasks.length,
          currentPhase: d.currentStage || (d.phases && d.phases.length > 0 ? d.phases[0].name : "30-Day Tracker"),
          phaseProgress: d.phases && d.phases.length > 0 ? Math.round((d.phases[0].items.filter(function(i) { return i.status === "done"; }).length / Math.max(d.phases[0].items.length, 1)) * 100) : 0,
        });
      }
      return true;
    } catch (err) {
      return false;
    }
  }, [onCountsUpdate]);

  // Write to cache after successful SharePoint fetch
  const writeToCache = useCallback(async function(tasks, blockers, phases) {
    try {
      await fetch(CACHE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tasks, blockers, phases }),
      });
    } catch (err) {
      console.warn("Cache write failed:", err.message);
    }
  }, []);

  const loadData = useCallback(async function() {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const [trackerData, phaseData] = await Promise.all([
        fetchPlatformTracker(token, projectConfig),
        fetchPhaseStatus(token, projectConfig),
      ]);
      const newTasks = trackerData.tasks.length > 0 ? trackerData.tasks : [];
      const newBlockers = trackerData.blockers.length > 0 ? trackerData.blockers : [];
      const newPhases = phaseData.phases.length > 0 ? phaseData.phases : [];
      if (newTasks.length > 0) setTasks(newTasks);
      if (newBlockers.length > 0) setBlockers(newBlockers);
      if (newPhases.length > 0) setPhases(newPhases);
      setLastUpdated(new Date().toISOString());
      setUsingCache(false);
      // Write to cache for unauthenticated users
      writeToCache(newTasks, newBlockers, newPhases);
      if (onCountsUpdate) {
        const t = newTasks.length > 0 ? newTasks : [];
        const p = newPhases.length > 0 ? newPhases : [];
        // currentStage comes from sheet order: sheet 2 is always the active stage
        const activeStage = trackerData.currentStage || (p.length > 0 ? p[0].name : "30-Day Tracker");
        onCountsUpdate({
          done: t.filter(function(i) { return i.status === "done"; }).length,
          inProg: t.filter(function(i) { return i.status === "in-progress"; }).length,
          notStarted: t.filter(function(i) { return i.status === "not-started"; }).length,
          atRisk: t.filter(function(i) { return i.risk; }).length,
          total: t.length,
          currentPhase: activeStage,
          phaseProgress: p.length > 0 ? Math.round((p[0].items.filter(function(i) { return i.status === "done"; }).length / Math.max(p[0].items.length, 1)) * 100) : 0,
        });
      }
    } catch (err) {
      // Auth failed or SharePoint error - try cache
      const gotCache = await loadFromCache();
      if (!gotCache) setError("Could not load live data. Sign in to refresh.");
    } finally {
      setLoading(false);
    }
  }, [getToken, onCountsUpdate, writeToCache, loadFromCache]);

  useEffect(function() { loadData(); }, []);

  const allTasks = tasks;
  const done = allTasks.filter(function(i) { return i.status === "done"; }).length;
  const inProg = allTasks.filter(function(i) { return i.status === "in-progress"; }).length;
  const notStarted = allTasks.filter(function(i) { return i.status === "not-started"; }).length;
  const atRisk = allTasks.filter(function(i) { return i.risk; }).length;
  const total = allTasks.length;

  // Expose live counts for banner
  const liveCounts = {
    done: tasks.filter(function(i) { return i.status === "done"; }).length,
    inProg: tasks.filter(function(i) { return i.status === "in-progress"; }).length,
    notStarted: tasks.filter(function(i) { return i.status === "not-started"; }).length,
    atRisk: tasks.filter(function(i) { return i.risk; }).length,
    total: tasks.length,
    currentPhase: phases.length > 0 ? phases[0].name : "Phase 1",
    phaseProgress: phases.length > 0 ? Math.round((phases[0].items.filter(function(i) { return i.status === "done"; }).length / Math.max(phases[0].items.length, 1)) * 100) : 0,
  };

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif", fontSize: 13 }}>

      {/* Live data header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 11, color: C.muted }}>
          {loading ? <Spinner small /> :
            lastUpdated ? "Live from SharePoint · " + new Date(lastUpdated).toLocaleTimeString() :
            usingCache && cachedAt ? "Cached data · Last updated " + new Date(cachedAt).toLocaleString() + " · Sign in to refresh" :
            "Loading..."}
        </div>
        {!loading && (
          <button onClick={loadData} style={{ padding: "5px 12px", border: "1px solid " + C.border, borderRadius: 4, background: C.white, cursor: "pointer", fontSize: 11, color: C.muted }}>
            ↻ Refresh
          </button>
        )}
      </div>

      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: C.red }}>
          {error} - showing last known data.
        </div>
      )}

      {/* Send Reminder Button - ACL only */}
      {userOnAcl && selectedCount > 0 && (
        <div style={{ position: "sticky", bottom: 16, zIndex: 10, display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <button onClick={function() { setShowReminderModal(true); }}
            style={{ background: C.dark, color: C.gold, border: "2px solid " + C.gold, borderRadius: 8, padding: "10px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 12px rgba(0,0,0,0.3)" }}>
            ✉ Send Reminder ({selectedCount} item{selectedCount > 1 ? 's' : ''})
          </button>
        </div>
      )}

      {/* Reminder Modal */}
      {showReminderModal && (function() {
        const recipients = buildRecipients();
        const aclPeople = Object.entries(REMINDER_ACL).filter(function(e) {
          return accounts[0] && e[1] !== accounts[0].username;
        });
        return (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ background: C.white, borderRadius: 12, padding: 24, width: 560, maxHeight: "85vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: C.dark, marginBottom: 4 }}>Send Status Update Reminder</div>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>{selectedCount} item{selectedCount > 1 ? 's' : ''} selected</div>

              {reminderSent ? (
                <div style={{ textAlign: "center", padding: "30px 0" }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
                  <div style={{ fontWeight: 700, color: C.green }}>Reminder sent successfully</div>
                </div>
              ) : (
                <>
                  {/* Selected items */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Items</div>
                    {selectedTaskItems.map(function(item, i) {
                      return (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 10px", background: "#f9fafb", borderRadius: 4, marginBottom: 4, fontSize: 12 }}>
                          <span style={{ color: C.dark }}>{item.task}</span>
                          <span style={{ color: C.muted }}>{item.owner}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* To recipients */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>To (resolved from owners)</div>
                    {recipients.length > 0 ? recipients.map(function(r) {
                      return (
                        <div key={r.email} style={{ padding: "4px 10px", background: "#e8f4fd", borderRadius: 4, marginBottom: 4, fontSize: 12, color: C.dark }}>
                          {r.name} &lt;{r.email}&gt;
                        </div>
                      );
                    }) : (
                      <div style={{ fontSize: 12, color: C.red, padding: "6px 10px", background: "#fef2f2", borderRadius: 4 }}>
                        No owners resolved. Check that owner names match the ACL in mailService.js.
                      </div>
                    )}
                  </div>

                  {/* Channel selector */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Send Via</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>

                      {/* Email */}
                      <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 6, border: "1px solid " + (notifyEmail ? C.gold : C.border), background: notifyEmail ? "#fffdf5" : "#f9fafb", cursor: "pointer" }}>
                        <input type="checkbox" checked={notifyEmail} onChange={function(e) { setNotifyEmail(e.target.checked); }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 12, color: C.dark }}>✉ Email</div>
                          <div style={{ fontSize: 10, color: C.muted }}>Sends directly to item owners</div>
                        </div>
                        {emailResult === "sent" && <span style={{ color: C.green, fontSize: 11, fontWeight: 700 }}>✓ Sent</span>}
                        {emailResult === "failed" && <span style={{ color: C.red, fontSize: 11, fontWeight: 700 }}>✗ Failed</span>}
                      </label>

                      {/* Teams */}
                      <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 6, border: "1px solid " + (notifyTeams ? "#2980b9" : C.border), background: notifyTeams ? "#e8f4fd" : "#f9fafb", cursor: "pointer" }}>
                        <input type="checkbox" checked={notifyTeams} onChange={function(e) { setNotifyTeams(e.target.checked); }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 12, color: C.dark }}>💬 Teams - TCC Platform Alerts</div>
                          <div style={{ fontSize: 10, color: C.muted }}>Posts an adaptive card to the channel</div>
                        </div>
                        {teamsResult === "sent" && <span style={{ color: C.green, fontSize: 11, fontWeight: 700 }}>✓ Sent</span>}
                        {teamsResult === "failed" && <span style={{ color: C.red, fontSize: 11, fontWeight: 700 }}>✗ Failed</span>}
                      </label>

                      {/* SMS */}
                      <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 6, border: "1px solid " + (notifySms ? C.green : C.border), background: notifySms ? "#f0fdf4" : "#f9fafb", cursor: "not-allowed", opacity: 0.6 }}>
                        <input type="checkbox" checked={notifySms} disabled onChange={function(e) { setNotifySms(e.target.checked); }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 12, color: C.dark }}>📱 SMS</div>
                          <div style={{ fontSize: 10, color: C.muted }}>Twilio not configured - coming Phase 2b</div>
                        </div>
                      </label>

                    </div>

                    {/* Email CC options - only show when email is selected */}
                    {notifyEmail && (
                      <div>
                        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.dark, marginBottom: 8, cursor: "pointer" }}>
                          <input type="checkbox" checked={reminderCc} onChange={function(e) { setReminderCc(e.target.checked); }} />
                          CC me ({accounts[0]?.username})
                        </label>
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>Additional recipients:</div>
                    {aclPeople.map(function(entry) {
                      return (
                        <label key={entry[1]} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.dark, marginBottom: 6, cursor: "pointer" }}>
                          <input type="checkbox"
                            checked={!!extraRecipients[entry[1]]}
                            onChange={function(e) {
                              setExtraRecipients(function(prev) {
                                const next = Object.assign({}, prev);
                                if (e.target.checked) next[entry[1]] = true;
                                else delete next[entry[1]];
                                return next;
                              });
                            }} />
                          {entry[0]} &lt;{entry[1]}&gt;
                        </label>
                      );
                    })}
                  </div>

                  {/* Actions */}
                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                    <button onClick={function() { setShowReminderModal(false); setSelectedItems({}); }}
                      style={{ padding: "8px 16px", border: "1px solid " + C.border, borderRadius: 6, background: C.white, cursor: "pointer", fontSize: 12, color: C.muted }}>
                      Cancel
                    </button>
                    <button onClick={function() { getToken().then(function(tok) { handleSendReminder(tok); }); }}
                      disabled={sendingReminder || (!notifyEmail && !notifyTeams && !notifySms)}
                      style={{ padding: "8px 20px", background: (!notifyEmail && !notifyTeams && !notifySms) ? "#9ca3af" : C.dark, color: C.gold, border: "none", borderRadius: 6, cursor: (!notifyEmail && !notifyTeams && !notifySms) ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700 }}>
                      {sendingReminder ? "Sending..." : "Send" + (notifyEmail && notifyTeams ? " Email + Teams" : notifyEmail ? " Email" : notifyTeams ? " to Teams" : "...")}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* Loading state */}
      {loading && tasks.length === 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "40px 20px", color: C.muted, justifyContent: "center" }}>
          <div style={{ width: 18, height: 18, border: "2px solid #e5e7eb", borderTop: "2px solid " + C.gold, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          <span style={{ fontSize: 13 }}>Loading live data from SharePoint...</span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Progress card */}
      {tasks.length > 0 && <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: 8, padding: "14px 16px", marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 4 }}>
          <div style={{ fontWeight: 700 }}>30-Day Tracker</div>
          <div style={{ color: C.muted }}>{done} of {total} complete | {inProg} in progress | {notStarted} not started</div>
        </div>
        <div style={{ background: "#f3f4f6", borderRadius: 4, height: 8 }}>
          <div style={{ width: (done > 0 ? Math.round((done / total) * 100) : 0) + "%", background: C.green, height: "100%", borderRadius: 4 }} />
        </div>
      </div>}

      {/* 30-Day Tracker - grouped by track */}
      {tasks.length > 0 && (function() {
        const tracks = {};
        tasks.forEach(function(item) {
          if (!tracks[item.track]) tracks[item.track] = [];
          tracks[item.track].push(item);
        });
        return (
          <div style={{ marginBottom: 14 }}>
            {Object.entries(tracks).map(function([track, items]) {
              return (
                <div key={track} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>{track}</div>
                  {items.map(function(item, i) {
                    return (
                      <TaskRow key={i} item={item} userOnAcl={userOnAcl}
                        selected={!!selectedItems["tracker::" + item.task]}
                        onToggle={function() { toggleItem("tracker::" + item.task); }}
                        onSave={function(updates) {
                          getToken().then(function(tok) {
                            return patchTrackerTask(tok, projectConfig, item.rowIndex, Object.assign({}, updates, { taskName: item.task }));
                          }).then(function() { loadData(); }).catch(function(e) { alert("Save failed: " + e.message); });
                        }}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })()}



      {/* Phase accordion - only shown when authenticated */}
      {phases.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.dark, marginBottom: 12 }}>Platform Roadmap - All Phases</div>
          {phases.map(function(phase, i) {
            const phaseBlockers = blockers.filter(function(b) {
              return b.phase && phase.name.toLowerCase().includes(b.phase.toLowerCase().replace('phase ', '').trim().split(' ')[0]);
            });
            return <PhaseAccordion key={phase.name} phase={phase} defaultOpen={i === 0} blockers={phaseBlockers} selectedItems={selectedItems} toggleItem={toggleItem} userOnAcl={userOnAcl} getToken={getToken} projectConfig={projectConfig} loadData={loadData} />;
          })}
        </div>
      )}

      {phases.length === 0 && !loading && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.dark, marginBottom: 12 }}>Platform Roadmap - All Phases</div>
          <div style={{ fontSize: 12, color: C.muted, padding: "20px 0" }}>Phase data loading... click Refresh if this persists.</div>
        </div>
      )}
    </div>
  );
}
